import React from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './legal.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { LegalPage } from './LegalPage';

const SECTIONS = [
  { id: 'overview', title: 'Overview', body: (
    <>
      <p>Rush Studio Ltd. ("Rush Studio", "we", "us") operates the translation workspace at rush.studio. This policy explains what personal data we collect, why we collect it, what we do with it, and what rights you have over it.</p>
      <p>We've tried to write this in plain English. The bits where we couldn't avoid the legal phrasing, we've marked. If anything is unclear, <Link to="/contact">tell us</Link> and we'll fix it in the next revision.</p>
      <div className="legal-callout"><strong>The short version:</strong> we collect the minimum data needed to run the service, we don't sell it, we encrypt it in transit and at rest, and we'll delete it when you ask.</div>
    </>
  ) },
  { id: 'data-we-collect', title: 'Data we collect', body: (
    <>
      <p>We collect three kinds of data:</p>
      <ul>
        <li><b>Account data</b> — your name, work email, company, and the password you set (hashed with bcrypt, never stored plaintext). Required to give you a workspace.</li>
        <li><b>Content data</b> — files you upload, segments you translate, glossaries, translation memory entries, comments. This is the data you're trusting us with; we treat it as confidential.</li>
        <li><b>Telemetry</b> — IP address, browser type, page views, feature usage, error reports. We use this to keep the service running and to figure out which features people actually use.</li>
      </ul>
      <p>We don't collect tracking IDs across other websites. We don't run ad networks. We don't profile you.</p>
    </>
  ) },
  { id: 'why-we-collect', title: 'Why we collect it', body: (
    <>
      <p>We process your data for one of these reasons:</p>
      <ul>
        <li><b>To provide the service</b> — translating your files, syncing with your integrations, sending you the emails you signed up for. This is a contractual necessity.</li>
        <li><b>Legitimate interest</b> — security, fraud prevention, debugging, product improvement. We do this in ways that don't compromise your privacy.</li>
        <li><b>Consent</b> — for optional marketing emails and any feature you've explicitly opted into. You can withdraw consent at any time.</li>
        <li><b>Legal obligation</b> — tax records, audit logs, anything else the law requires us to keep.</li>
      </ul>
    </>
  ) },
  { id: 'ai-providers', title: 'AI translation providers', body: (
    <>
      <p>To produce machine-translation drafts, we route segments of your content to one or more AI providers (DeepL, Google Cloud Translation, OpenAI, Anthropic, Microsoft Translator, Amazon Translate). Which provider we use depends on the language pair — see the <Link to="/languages">languages page</Link> for the per-language breakdown.</p>
      <p>We send <strong>only</strong> the segment being translated, plus any glossary terms you've configured. We don't send file names, account info, or other context. All providers we use contractually agree not to train their models on your data, and to delete content after the translation is returned.</p>
      <p>If you'd rather your data never leave Rush Studio's infrastructure, our Scale plan offers an on-premises translation engine; <Link to="/contact">talk to sales</Link>.</p>
    </>
  ) },
  { id: 'where-we-store', title: 'Where we store data', body: (
    <>
      <p>Rush Studio is operated from the European Union. Primary data storage is in <strong>Frankfurt, Germany</strong> (eu-central-1). Backups are replicated to <strong>Dublin, Ireland</strong> (eu-west-1). We do not store production data in the United States.</p>
      <p>Some of our sub-processors are based outside the EU. Where personal data is transferred internationally, we rely on Standard Contractual Clauses (SCCs) as approved by the European Commission. See our <Link to="/gdpr">sub-processor list</Link> for details.</p>
    </>
  ) },
  { id: 'retention', title: 'How long we keep data', body: (
    <>
      <p>We keep your data only as long as we need it:</p>
      <ul>
        <li><b>Account data</b> — for the lifetime of your account, plus 30 days after deletion (in case you change your mind).</li>
        <li><b>Content data</b> — same as account data; deleted within 30 days of account closure, or sooner if you delete the project explicitly.</li>
        <li><b>Translation memory & glossaries</b> — yours to keep; exportable in TMX/CSV at any time, deleted alongside the account.</li>
        <li><b>Telemetry</b> — 13 months in identifiable form, then aggregated.</li>
        <li><b>Billing records</b> — 7 years, as required by tax law in our jurisdiction.</li>
      </ul>
    </>
  ) },
  { id: 'your-rights', title: 'Your rights', body: (
    <>
      <p>Under GDPR and equivalent laws elsewhere, you have the right to:</p>
      <ul>
        <li><b>Access</b> the personal data we hold about you — request a copy from your settings.</li>
        <li><b>Rectify</b> data that's inaccurate.</li>
        <li><b>Erase</b> your data, subject to the retention rules above and any legal obligations.</li>
        <li><b>Restrict</b> processing while a complaint is being resolved.</li>
        <li><b>Object</b> to processing based on legitimate interest.</li>
        <li><b>Portability</b> — receive your data in a machine-readable format. We provide JSON + TMX exports.</li>
        <li><b>Lodge a complaint</b> with your data protection authority. In the EU, that's typically your country's DPA.</li>
      </ul>
      <p>Most of these are one click in your account settings. For anything more involved, email <a href="mailto:privacy@rushstudio.com">privacy@rushstudio.com</a> and we'll respond within 30 days (usually much sooner).</p>
    </>
  ) },
  { id: 'cookies', title: 'Cookies & similar technologies', body: (
    <>
      <p>We use a small number of strictly-necessary cookies to keep you signed in and to remember your workspace preferences. We don't use advertising cookies, third-party trackers, or fingerprinting.</p>
      <p>The only optional cookie is for our product analytics, and we ask before setting it. You can decline; the service works the same.</p>
    </>
  ) },
  { id: 'security', title: 'Security', body: (
    <>
      <p>We follow industry-standard practices: TLS 1.3 in transit, AES-256 at rest, hashed passwords, role-based access controls, audit logs, and SOC 2 Type II certification. Our infrastructure runs on AWS in EU regions.</p>
      <p>We run pentests annually with an external vendor. The most recent report is available under NDA — email <a href="mailto:security@rushstudio.com">security@rushstudio.com</a>.</p>
    </>
  ) },
  { id: 'children', title: 'Children', body: (
    <>
      <p>Rush Studio is a B2B product. We don't knowingly collect data from anyone under 16, and there's no reason for them to be using us. If you believe a minor has signed up, write to us at <a href="mailto:privacy@rushstudio.com">privacy@rushstudio.com</a> and we'll delete the account.</p>
    </>
  ) },
  { id: 'changes', title: 'Changes to this policy', body: (
    <>
      <p>We'll email everyone with an active account if we make a material change. We'll post the change here at least 30 days before it takes effect, and we keep a public changelog of revisions at <a href="#">rush.studio/privacy/changelog</a>.</p>
      <p>Trivial fixes — typos, broken links, clarifications that don't change meaning — we make silently.</p>
    </>
  ) },
  { id: 'contact', title: 'Contact', body: (
    <>
      <p><strong>Data Controller:</strong> Rush Studio Ltd., 12 Hatton Garden, London EC1N 8AT, United Kingdom.</p>
      <p><strong>Data Protection Officer:</strong> Pedro Almeida — <a href="mailto:dpo@rushstudio.com">dpo@rushstudio.com</a>.</p>
      <p><strong>General privacy questions:</strong> <a href="mailto:privacy@rushstudio.com">privacy@rushstudio.com</a>. We respond within one business day.</p>
    </>
  ) },
];

const RushPrivacyPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav />
      <LegalPage
        eyebrow="Legal · Privacy"
        title="Privacy Policy"
        updated="May 20, 2026"
        intro="How Rush Studio collects, uses, and protects your data. Written to be readable, not bulletproof — but reviewed by counsel to make sure it actually says what it means."
        sections={SECTIONS}
        related={[
          { href: '/terms', label: 'Terms of Service' },
          { href: '/gdpr', label: 'GDPR & DPA' },
          { href: '/contact', label: 'Contact us' },
        ]}
      />
      <RushFooter />
    </div>
  );
};

export default RushPrivacyPage;
